import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { ClipboardList, ExternalLink, RefreshCw, AlertTriangle, CheckCircle2, FileSignature, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PANEL_LABELS = {
  front: 'Front',
  rear: 'Rear',
  driver_side: 'Driver Side',
  passenger_side: 'Passenger Side',
  hood: 'Hood',
  trunk: 'Trunk',
  roof: 'Roof',
  interior: 'Interior',
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

const ConditionReportsTab = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [active, setActive] = useState(null);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API}/api/condition-reports?limit=200`);
        const d = await r.json();
        if (!cancelled) setReports(d.reports || []);
      } catch { if (!cancelled) toast.error('Failed to load reports'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const load = () => setReloadKey(k => k + 1);

  const filtered = filter ? reports.filter(r => r.type === filter) : reports;
  const counts = {
    total: reports.length,
    pre: reports.filter(r => r.type === 'pre').length,
    post: reports.filter(r => r.type === 'post').length,
    signed: reports.filter(r => r.status === 'signed').length,
    flagged: reports.filter(r => Object.values(r.panels || {}).some(p => p.damage)).length,
  };

  return (
    <div className="space-y-4 p-2">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black tracking-tight">Vehicle Condition Reports</h2>
          <p className="text-xs text-gray-500 mt-1">Pre/post walkaround inspections captured by staff.</p>
        </div>
        <button onClick={load} className="h-9 px-3 rounded-md bg-gray-100 hover:bg-gray-200 text-xs font-bold flex items-center gap-1.5" data-testid="cr-reload">
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Reload
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="All" value={counts.total} onClick={() => setFilter('')} active={!filter} />
        <Stat label="Pre-Rental" value={counts.pre} onClick={() => setFilter('pre')} active={filter === 'pre'} />
        <Stat label="Post-Rental" value={counts.post} onClick={() => setFilter('post')} active={filter === 'post'} />
        <Stat label="Signed" value={counts.signed} color="emerald" />
        <Stat label="Damage Flagged" value={counts.flagged} color="amber" />
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Type</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Vehicle</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Client</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Mileage</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Status</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Captured</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400"><ClipboardList className="h-8 w-8 mx-auto mb-2 text-gray-300" /> No reports yet.</td></tr>
              ) : filtered.map(r => {
                const hasDamage = Object.values(r.panels || {}).some(p => p.damage);
                return (
                  <tr key={r.report_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${r.type === 'pre' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{r.type}-rental</span></td>
                    <td className="px-3 py-2 font-semibold">{r.vehicle_name || '—'}</td>
                    <td className="px-3 py-2">{r.client_name || '—'}</td>
                    <td className="px-3 py-2 font-mono">{r.mileage ?? '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${r.status === 'signed' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                        {r.status === 'signed' && <CheckCircle2 className="h-3 w-3 inline mr-0.5" />}
                        {r.status}
                      </span>
                      {hasDamage && (
                        <span className="ml-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                          <AlertTriangle className="h-3 w-3 inline mr-0.5" /> Damage
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(r.created_at)}<br /><span className="text-gray-400">by {r.created_by}</span></td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setActive(r)} className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1 ml-auto" data-testid={`cr-view-${r.report_id}`}>
                        View <ExternalLink className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {active && <ReportDrawer report={active} onClose={() => setActive(null)} />}
    </div>
  );
};

const Stat = ({ label, value, onClick, active, color }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className={`p-3 rounded-lg border text-left transition-all ${active ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'} ${!onClick && 'cursor-default'}`}
  >
    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</p>
    <p className={`text-2xl font-black mt-0.5 ${color === 'emerald' ? 'text-emerald-600' : color === 'amber' ? 'text-amber-600' : 'text-gray-900'}`}>{value}</p>
  </button>
);

const ReportDrawer = ({ report, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{report.type}-rental Inspection</p>
            <h3 className="text-xl font-black mt-1">{report.vehicle_name}</h3>
            <p className="text-sm text-gray-500">{report.client_name} · {fmtDate(report.created_at)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Field label="Mileage" value={report.mileage ?? '—'} />
            <Field label="Fuel" value={report.fuel_level || '—'} />
            <Field label="Status" value={report.status} />
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">Panels</p>
            <div className="space-y-2">
              {Object.entries(report.panels || {}).map(([key, p]) => (
                <div key={key} className={`p-3 rounded-lg border ${p.damage ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-200'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm">{PANEL_LABELS[key] || key}</span>
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${p.damage ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'}`}>
                      {p.damage ? 'Damage' : 'OK'}
                    </span>
                  </div>
                  {p.notes && <p className="text-xs text-gray-700 mt-1">{p.notes}</p>}
                  {(p.photos || []).length > 0 && (
                    <div className="grid grid-cols-4 gap-1.5 mt-2">
                      {p.photos.map((ph, i) => (
                        <a key={i} href={`${API}${ph.url}`} target="_blank" rel="noreferrer" className="block aspect-square bg-gray-100 rounded overflow-hidden border">
                          <img src={`${API}${ph.url}`} alt={`Panel ${key} ${i + 1}`} className="w-full h-full object-cover hover:opacity-80" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {report.general_notes && (
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1">General Notes</p>
              <p className="text-sm text-gray-700 bg-gray-50 rounded p-3 border border-gray-200">{report.general_notes}</p>
            </div>
          )}

          {report.signature_dataurl && (
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2 flex items-center gap-1.5">
                <FileSignature className="h-3 w-3" /> Signed by {report.signed_by || 'Client'}
              </p>
              <div className="bg-gray-900 rounded p-3 border">
                <img src={report.signature_dataurl} alt="Signature" className="max-h-32 w-auto mx-auto" />
              </div>
              <p className="text-[10px] text-gray-500 mt-1">Captured {fmtDate(report.signed_at)}</p>
            </div>
          )}

          <div className="text-[10px] text-gray-400 pt-2 border-t border-gray-200">
            Report ID · {report.report_id} · created by {report.created_by}
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div className="p-2 rounded-lg bg-gray-50 border border-gray-200">
    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</p>
    <p className="text-base font-black text-gray-900 mt-0.5">{value}</p>
  </div>
);

export default ConditionReportsTab;
